import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ArrowUp, ArrowDown, ArrowUpDown, Plus, Users } from 'lucide-react';
import { useClientsList, useClientsCounts, type ClientListRow } from '@/api/clients';
import { useUserDirectory } from '@/api/users';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/FormField';
import { StatusBadge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/cn';
import { AuthProgressBar } from './AuthProgressBar';
import { AuthExpiryIndicator } from './AuthExpiryIndicator';
import { STATUS_TABS, type StatusTab, calcAge, fmtDob, authExpiryWarning } from './clientHelpers';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
type SortKey = 'name' | 'dob' | 'bcba' | 'status';
type SortDir = 'asc' | 'desc';

function clientInitials(c: ClientListRow) {
    return (c.first[0] || '') + (c.last[0] || '');
}

export function ClientsListPage() {
    const canAdd = useAuthStore((s) => s.hasPermission('add_clients'));
    const navigate = useNavigate();
    const { data, isLoading, isError, error } = useClientsList();
    // Counts per status bucket come from a dedicated aggregate endpoint so the
    // tabs can show totals without loading (or filtering) every row.
    const { data: counts } = useClientsCounts();
    // Staff filter options — every active staff user in the practice, because
    // any of them can be either the assigned BCBA or an additional staff
    // member on a client. The directory endpoint already excludes parent
    // accounts server-side.
    const { data: directory } = useUserDirectory();

    const [tab, setTab] = useState<StatusTab>('Active');
    const [search, setSearch] = useState('');
    const [staffFilter, setStaffFilter] = useState<string>('');
    const [payerFilter, setPayerFilter] = useState<string>('');
    const [sortKey, setSortKey] = useState<SortKey>('name');
    const [sortDir, setSortDir] = useState<SortDir>('asc');

    // Distinct payers across the visible client list — drives the payer
    // dropdown. Clients with no payer ('') are hidden from the options but
    // can still be targeted via the "— None —" entry below.
    const payerOptions = useMemo(() => {
        if (!data) return [] as string[];
        const set = new Set<string>();
        for (const c of data) {
            if (c.payer) set.add(c.payer);
        }
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [data]);

    const rows = useMemo(() => {
        if (!data) return [];
        const q = search.trim().toLowerCase();
        const filtered = data.filter((c) => {
            if (tab !== 'All' && c.status !== tab) return false;
            if (q) {
                const name = `${c.first} ${c.last}`.toLowerCase();
                if (!name.includes(q)) return false;
            }
            if (staffFilter) {
                // Matches if the selected user is the assigned BCBA or appears
                // in the additional-staff list. The server returns bcbaId in
                // staffUserIds too, so a single `includes` check covers both.
                if (!c.staffUserIds.includes(staffFilter)) return false;
            }
            if (payerFilter) {
                if (payerFilter === '__none__') {
                    if (c.payer) return false;
                } else if (c.payer !== payerFilter) {
                    return false;
                }
            }
            return true;
        });
        const sorted = [...filtered].sort((a, b) => {
            let cmp = 0;
            if (sortKey === 'name') {
                cmp = (a.last || '').localeCompare(b.last || '')
                    || (a.first || '').localeCompare(b.first || '');
            } else if (sortKey === 'dob') {
                cmp = (a.dob || '').localeCompare(b.dob || '');
            } else if (sortKey === 'bcba') {
                cmp = (a.bcba || '').localeCompare(b.bcba || '');
            } else if (sortKey === 'status') {
                cmp = (a.status || '').localeCompare(b.status || '');
            }
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return sorted;
    }, [data, tab, search, staffFilter, payerFilter, sortKey, sortDir]);

    function toggleSort(key: SortKey) {
        if (key === sortKey) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    }

    return (
        <div className="p-6 max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between mb-4">
                <h1 className="font-display text-2xl font-bold text-text">Clients</h1>
                {canAdd && (
                    <Button variant="primary" size="sm" onClick={() => navigate('/clients/new')}>
                        <Plus className="w-3.5 h-3.5" /> Add client
                    </Button>
                )}
            </div>

            <Card className="p-0 overflow-hidden">
                <div className="flex flex-wrap items-center gap-3 p-3 border-b border-border">
                    <div role="tablist" aria-label="Filter clients by status" className="flex items-center gap-1 p-0.5 bg-surface-muted rounded-md">
                        {STATUS_TABS.map((s) => {
                            const n = s === 'All' ? counts?.total : counts?.byStatus[s];
                            return (
                                <button
                                    key={s}
                                    type="button"
                                    role="tab"
                                    aria-selected={tab === s}
                                    onClick={() => setTab(s)}
                                    className={cn(
                                        'px-3 py-1 text-xs rounded transition-colors',
                                        tab === s
                                            ? 'bg-surface text-text shadow-xs font-medium'
                                            : 'text-text-muted hover:text-text',
                                    )}
                                >
                                    {s}
                                    {typeof n === 'number' && (
                                        <span className="ml-1.5 text-text-subtle tabular-nums">{n}</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    <div className="relative flex-1 min-w-[220px] max-w-xs">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-subtle" />
                        <Input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by name…"
                            aria-label="Search clients by name"
                            className="pl-8 h-8 py-1"
                        />
                    </div>
                    <select
                        value={staffFilter}
                        onChange={(e) => setStaffFilter(e.target.value)}
                        aria-label="Filter by assigned staff"
                        className="h-8 px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                    >
                        <option value="">All staff</option>
                        {directory?.map((u) => (
                            <option key={u.id} value={u.id}>
                                {u.first} {u.last}
                                {u.roleType === 'BCBA' || u.roleType === 'RBT' ? ` · ${u.roleType}` : ''}
                            </option>
                        ))}
                    </select>
                    <select
                        value={payerFilter}
                        onChange={(e) => setPayerFilter(e.target.value)}
                        aria-label="Filter by payer"
                        className="h-8 px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                    >
                        <option value="">All payers</option>
                        <option value="__none__">— None —</option>
                        {payerOptions.map((p) => (
                            <option key={p} value={p}>{p}</option>
                        ))}
                    </select>
                    <div className="ml-auto text-xs text-text-subtle tabular-nums">
                        {rows.length} {rows.length === 1 ? 'client' : 'clients'}
                    </div>
                </div>

                {isLoading ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                    <th className="px-3 py-2 font-bold">Client</th>
                                    <th className="px-3 py-2 font-bold">DOB / Age</th>
                                    <th className="px-3 py-2 font-bold">Diagnosis</th>
                                    <th className="px-3 py-2 font-bold">BCBA</th>
                                    <th className="px-3 py-2 font-bold">Payer</th>
                                    <th className="px-3 py-2 font-bold">Auth</th>
                                    <th className="px-3 py-2 font-bold">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Array.from({ length: 8 }).map((_, i) => (
                                    <tr key={i} className="border-b border-border last:border-b-0">
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-2">
                                                <Skeleton className="w-7 h-7 rounded-full" />
                                                <Skeleton className="h-3.5 w-28" />
                                            </div>
                                        </td>
                                        <td className="px-3 py-2"><Skeleton className="h-3 w-20" /></td>
                                        <td className="px-3 py-2"><Skeleton className="h-3 w-16" /></td>
                                        <td className="px-3 py-2"><Skeleton className="h-3 w-24" /></td>
                                        <td className="px-3 py-2"><Skeleton className="h-3 w-20" /></td>
                                        <td className="px-3 py-2"><Skeleton className="h-2 w-24 rounded-full" /></td>
                                        <td className="px-3 py-2"><Skeleton className="h-5 w-14 rounded-full" /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : isError ? (
                    <div className="p-12 text-center text-error">
                        <p className="font-medium mb-1">Couldn't load clients</p>
                        <p className="text-xs text-text-subtle">
                            {extractErrorMessage(error, 'Please try again.')}
                        </p>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-12 flex flex-col items-center gap-3 text-center">
                        <Users className="w-8 h-8 text-text-subtle" aria-hidden />
                        {data && data.length > 0 ? (
                            <>
                                <p className="text-sm font-medium text-text">No clients match the current filters</p>
                                <p className="text-xs text-text-subtle">Try adjusting the status tab, search, or filter options.</p>
                            </>
                        ) : (
                            <>
                                <p className="text-sm font-medium text-text">No clients yet</p>
                                <p className="text-xs text-text-subtle">Add your first client to get started.</p>
                                {canAdd && (
                                    <Button variant="primary" size="sm" onClick={() => navigate('/clients/new')}>
                                        <Plus className="w-3.5 h-3.5" /> Add your first client
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                    <SortableTh label="Client"    sortKey="name"   current={sortKey} dir={sortDir} onToggle={toggleSort} />
                                    <SortableTh label="DOB / Age" sortKey="dob"    current={sortKey} dir={sortDir} onToggle={toggleSort} />
                                    <th className="px-3 py-2 font-bold">Diagnosis</th>
                                    <SortableTh label="BCBA"      sortKey="bcba"   current={sortKey} dir={sortDir} onToggle={toggleSort} />
                                    <th className="px-3 py-2 font-bold">Payer</th>
                                    <th className="px-3 py-2 font-bold">Auth</th>
                                    <SortableTh label="Status"    sortKey="status" current={sortKey} dir={sortDir} onToggle={toggleSort} />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((c) => {
                                    const age = calcAge(c.dob);
                                    const authWarning = authExpiryWarning(c.status, c.auth);
                                    return (
                                        <tr
                                            key={c.id}
                                            onClick={(e) => {
                                                if (e.defaultPrevented) return;
                                                // Modified clicks (ctrl/cmd/middle) are handled by the name <Link>
                                                // so open-in-new-tab still works — don't hijack them here.
                                                if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
                                                navigate(`/clients/${c.id}`);
                                            }}
                                            className="border-b border-border last:border-b-0 hover:bg-surface-muted transition-colors cursor-pointer"
                                        >
                                            <td className="px-3 py-2">
                                                <Link
                                                    to={`/clients/${c.id}`}
                                                    className="flex items-center gap-2 focus:outline-none"
                                                >
                                                    <span
                                                        aria-hidden
                                                        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-accent text-[#111] text-2xs font-bold"
                                                    >
                                                        {clientInitials(c)}
                                                    </span>
                                                    <span className="font-medium hover:underline">
                                                        {c.first} {c.last}
                                                    </span>
                                                </Link>
                                            </td>
                                            <td className="px-3 py-2 text-text-muted whitespace-nowrap">
                                                {fmtDob(c.dob)}
                                                {age !== null && <span className="text-text-subtle"> · {age}y</span>}
                                            </td>
                                            <td className="px-3 py-2 text-text-muted">
                                                {c.dx1}{c.dx2 ? `; ${c.dx2}` : ''}
                                            </td>
                                            <td className="px-3 py-2 text-text-muted whitespace-nowrap">{c.bcba || '—'}</td>
                                            <td className="px-3 py-2 text-text-muted">{c.payer || '—'}</td>
                                            <td className="px-3 py-2">
                                                <AuthProgressBar auth={c.auth} />
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="flex items-center gap-1.5">
                                                    <StatusBadge status={c.status} />
                                                    <AuthExpiryIndicator warning={authWarning} />
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
}

function SortableTh({
    label, sortKey, current, dir, onToggle,
}: {
    label: string;
    sortKey: SortKey;
    current: SortKey;
    dir: SortDir;
    onToggle: (key: SortKey) => void;
}) {
    const active = current === sortKey;
    const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
    return (
        <th className="px-3 py-2 font-bold">
            <button
                type="button"
                onClick={() => onToggle(sortKey)}
                className={cn(
                    'inline-flex items-center gap-1 uppercase tracking-wider text-2xs',
                    active ? 'text-text' : 'text-text-subtle hover:text-text',
                )}
                aria-label={`Sort by ${label}${active ? ` (${dir === 'asc' ? 'ascending' : 'descending'})` : ''}`}
            >
                {label}
                <Icon className="w-3 h-3" />
            </button>
        </th>
    );
}
